import React, { useMemo, useCallback } from "react";
import { toast } from "react-toastify";
import Button from "../components/buttons/Button";
import { Form, Formik } from "formik";
import AsyncFormikDropdown from "../components/inputFields/AsyncFormikDropDown";
import FormikDropDown from "../components/inputFields/FormikDropDown";
import InputField from "../components/inputFields/InputField";
import { patchUserSegrigation, postUserSegrigation } from "../services/farmers";
import { getUserId } from "../helpers/local_storage_helpers";

// Main Component
function UserFeedbackPop({ item, onUpdateAny }) {
    // Memoized segmentation data to prevent re-calculation on every render
    const segmentation = useMemo(() => {
        if (!item || !item.segmentations || !Array.isArray(item.segmentations) || item.segmentations.length === 0) {
            return { seg_type: "farmer", feedback: "" };
        }
        return item.segmentations[0];
    }, [item]);

    // Segmentation type options - memoized to prevent recreation
    const segmentationOptions = useMemo(() => [
        { label: "Farmer", value: "farmer" },
        { label: "Business", value: "business" },
        { label: "Retailer", value: "retailer" },
        { label: "Distributor", value: "distributor" },
        { label: "Other", value: "other" }
    ], []);

    const handleFeedbackSubmit = useCallback(async (values) => {
        // Safety check - if item becomes null/undefined during submission, abort
        if (!item || !item.id) {
            toast.error("User data not available");
            return;
        }

        try {
            let response;
            // Only send seg_type and feedback in the payload
            const payload = (item?.segmentations?.length || 0) > 0 ? {
                feedback: values.feedback,
                user: item.id,
                staff: getUserId()
            } : {
                seg_type: values.seg_type === "other" ? values.custom_seg_type : values.seg_type,
                feedback: values.feedback,
                user: item.id,
                staff: getUserId()
            };

            if (item.id && item.segmentations && item.segmentations[0]?.id) {
                response = await patchUserSegrigation({ data: payload, id: item.segmentations[0].id });
            } else {
                response = await postUserSegrigation({ data: payload });
            }

            if (response && (response.status === 200 || response.status === 201)) {
                toast.success("Feedback updated successfully");
            } else {
                toast.error("Unable to update feedback");
            }

            if (onUpdateAny) {
                onUpdateAny();
            }
        } catch (error) {
            console.error("Error:", error);
            toast.error("Unable to update feedback");
        }
    }, [item, onUpdateAny]);

    // Memoized initial values to prevent Formik re-initialization
    const initialValues = useMemo(() => ({
        feedback: segmentation?.feedback || "",
        seg_type: segmentation?.seg_type || "farmer",
        custom_seg_type: ""
    }), [segmentation]);

    // Safety check for item prop
    if (!item) {
        return (
            <div className="p-3 space-y-3 bg-gradient-to-br from-gray-50 to-blue-50/30 min-h-screen max-w-7xl mx-auto">
                <div className="bg-white rounded-xl shadow-md border border-gray-200/50 p-4 text-center">
                    <p className="text-gray-500">No user data available</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-3 space-y-3 bg-gradient-to-br from-gray-50 to-blue-50/30 min-h-screen max-w-7xl mx-auto">
            {/* Enhanced Header Section */}
            <div className="bg-white rounded-xl shadow-md border border-gray-200/50 p-4">
                <div>
                    <h1 className="text-xl font-bold text-gray-900 mb-1">
                        {item?.first_name || ''} {item?.last_name || ''}
                    </h1>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                        {item?.mobile_number && (
                            <div className="flex items-center gap-1">
                                <span className="text-blue-500">📱</span>
                                <span>{item.mobile_number}</span>
                            </div>
                        )}
                        {item?.address && (
                            <div className="flex items-center gap-1">
                                <span className="text-green-500">📍</span>
                                <span className="truncate max-w-48">{item.address}</span>
                            </div>
                        )}
                        {item?.registration_date && (
                            <div className="flex items-center gap-1">
                                <span className="text-purple-500">📅</span>
                                <span>Since {new Date(item.registration_date).toLocaleDateString()}</span>
                            </div>
                        )}
                    </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200">
                    <h3 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                        <span className="text-blue-500">💬</span>
                        User Feedback & Segmentation
                    </h3>
                    <Formik
                        initialValues={initialValues}
                        onSubmit={handleFeedbackSubmit}
                        enableReinitialize={true}
                    >
                        {({ isSubmitting, values, setFieldValue }) => (
                            <Form className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Segmentation Type Dropdown */}
                                    <div>
                                        {
                                            item?.segmentations && item?.segmentations?.length > 0 ? (
                                                <p className="text-primary font-bold">User Type: {item?.segmentations[0]?.seg_type?.toUpperCase()}</p>
                                            ) : (
                                                <FormikDropDown
                                                    name="seg_type"
                                                    label="Segmentation Type"
                                                    placeholder="Select type"
                                                    options={segmentationOptions}
                                                    required={true}
                                                />
                                            )
                                        }
                                    </div>

                                    {/* Custom Segmentation Type Field - Show only when "other" is selected */}
                                    {values.seg_type === "other" && (
                                        <div>
                                            <InputField
                                                name="custom_seg_type"
                                                label="Custom Segmentation Type"
                                                placeholder="Enter custom type"
                                                required={true}
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Feedback Field */}
                                <div>
                                    <InputField
                                        name="feedback"
                                        label="Feedback"
                                        placeholder="Enter feedback"
                                        required={true}
                                        multiline={true}
                                        rows={3}
                                    />
                                </div>

                                {/* Action Buttons */}
                                <div className="flex gap-3 pt-2">
                                    <Button
                                        children={isSubmitting ? "Updating..." : "Update Feedback"}
                                        type="submit"
                                        className="bg-gradient-to-r from-primary to-blue-600 text-white px-6 py-2 rounded-lg hover:shadow-md transition-all duration-200 transform hover:scale-105"
                                        disabled={isSubmitting}
                                    />
                                    <Button
                                        children="Reset"
                                        type="button"
                                        className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                                        onClick={() => {
                                            setFieldValue("feedback", segmentation?.feedback || "");
                                            setFieldValue("seg_type", segmentation?.seg_type || "farmer");
                                            setFieldValue("custom_seg_type", "");
                                        }}
                                    />
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>
        </div>
    );
}

export default UserFeedbackPop;